import { Form, Link } from "@remix-run/react";
import { useState } from "react";
import { Button } from "~/components/common/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "~/components/common/dialog";
import { Input } from "~/components/common/input";
import { Logo } from "~/components/logo";
import { ThreeDots } from "~/components/three-dots";
import { Button as HeadlessButton, Field } from "@headlessui/react";
import { FieldGroup, Label } from "~/components/common/fieldset";
import { ActionFunctionArgs, MetaFunction, redirect } from "@remix-run/node";
import { sendDemoEmail } from "~/.server/utils/email";

export async function action(args: ActionFunctionArgs) {
  const formData = await args.request.formData();
  const name = formData.get("name") as string;
  const email = formData.get("email") as string;
  const timezone = formData.get("timezone") as string;

  await sendDemoEmail({
    name,
    email,
    timezone,
  });

  return redirect("/thank-you");
}

function AdvantageItem({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className={`flex flex-col gap-6 sm:gap-4 relative`}>
      <h3
        className="text-xl md:text-lg lg:text-2xl text-brandRed"
        style={{ fontFamily: "Rokkitt Variable" }}
      >
        {title}
        <span className="animate-pulse"> _</span>
      </h3>
      <p className="text-sm">{description}</p>
    </div>
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "Inkling | Write documents with embedded survey questions" },
  ];
};

export default function Home() {
  const [header1Hover, setHeader1Hover] = useState(false);
  const [header2Hover, setHeader2Hover] = useState(false);
  const [header3Hover, setHeader3Hover] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col bg-brandBackground min-h-screen">
      <header>
        <div className="flex items-center justify-between">
          <div className="max-w-40 sm:max-w-52 mx-10 h-20 sm:mb-[-25px] mb-0 z-50">
            <Logo forceLightMode={true} />
          </div>
          <nav className="flex flex-row gap-10 mx-10">
            <ul>
              <li>
                <Link
                  to="/sign-in"
                  style={{ fontFamily: "Rokkitt Variable" }}
                  className="hover:underline hover:text-brandRed transition-colors duration-300 text-lg"
                >
                  Sign in
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </header>
      <section className=" bg-brandRed text-brandWhite sm:min-h-[600px]">
        <div className="flex flex-col md:flex-row items-center md:items-end justify-center p-6 sm:p-10 gap-10 md:gap-20">
          <div
            className="flex flex-col text-2xl sm:text-3xl md:text-4xl lg:text-[40px] max-w-xl self-start md:self-end"
            style={{ fontFamily: "Raleway Variable", lineHeight: "1.2" }}
          >
            <h1>
              Create documents{" "}
              <sup
                className="text-sm sm:text-md lg:pb-2 hover:underline"
                style={{ verticalAlign: "50%" }}
                onMouseOver={() => setHeader1Hover(true)}
                onMouseLeave={() => setHeader1Hover(false)}
                onFocus={() => setHeader1Hover(true)}
                onBlur={() => setHeader1Hover(false)}
              >
                1
              </sup>{" "}
              that include survey questions{" "}
              <sup
                className="text-sm sm:text-md lg:pb-2 hover:underline"
                style={{ verticalAlign: "50%" }}
                onMouseOver={() => setHeader2Hover(true)}
                onMouseLeave={() => setHeader2Hover(false)}
                onFocus={() => setHeader2Hover(true)}
                onBlur={() => setHeader2Hover(false)}
              >
                2
              </sup>{" "}
              so you can gather feedback{" "}
              <sup
                className="text-sm sm:text-md lg:pb-2 hover:underline"
                style={{ verticalAlign: "50%" }}
                onMouseOver={() => setHeader3Hover(true)}
                onMouseLeave={() => setHeader3Hover(false)}
                onFocus={() => setHeader3Hover(true)}
                onBlur={() => setHeader3Hover(false)}
              >
                3
              </sup>{" "}
              and make informed decisions.
            </h1>
          </div>
          <div className="flex flex-col self-start font-mono gap-4 sm:gap-6 max-w-sm text-xs sm:text-sm ">
            <div
              className={`${header2Hover ? "opacity-50" : ""} ${
                header3Hover ? "opacity-50" : ""
              } transition-opacity duration-300`}
            >
              1. <br />
              Write in Markdown or import a PDF. You can embed Figma prototypes,
              Loom videos, or anything else you have.
            </div>
            <div
              className={`${header1Hover ? "opacity-50" : ""} ${
                header3Hover ? "opacity-50" : ""
              } transition-opacity duration-300`}
            >
              2. <br />
              Documents can including questions inline. Choose from multiple
              choice, text, opinion scales, and other question types.
            </div>
            <div
              className={`${header1Hover ? "opacity-50" : ""} ${
                header2Hover ? "opacity-50" : ""
              } transition-opacity duration-300`}
            >
              3. <br />
              Generate different links for different audiences so that you can
              easily track open and response rates.
            </div>
            <div className="flex flex-row gap-4 mt-5">
              <Link
                to="/sign-up"
                className="bg-brandWhite text-brandRed px-3 py-1 sm:px-4 sm:py-2 rounded-md text-sm hover:underline hover:font-medium transition-all"
              >
                Sign up
              </Link>
              <HeadlessButton
                onClick={() => setIsOpen(true)}
                className="bg-brandWhite/20 text-brandWhite px-3 py-1 sm:px-4 sm:py-2 rounded-md text-sm hover:underline hover:font-medium transition-all"
              >
                Request a demo
              </HeadlessButton>
            </div>
          </div>
        </div>
      </section>
      <section className="flex flex-row justify-center p-4 sm:p-10">
        <div className="relative max-w-5xl w-full bg-brandYellow transform h-auto md:mt-[-8rem] rounded-md border-2 border-brandYellow">
          <video
            src={
              "https://res.cloudinary.com/inkling-media/video/upload/f_mp4,vc_h265,q_auto/v1728539236/u9armcrevkwa0sobvbes"
            }
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
        </div>
      </section>
      <section className="flex flex-col justify-center p-6 sm:p-10 md:p-20 items-center gap-6 sm:gap-10">
        <div className="flex flex-col gap-2">
          <ThreeDots />
        </div>
        <h2
          className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center"
          style={{ fontFamily: "Raleway Variable" }}
        >
          the inkling process
        </h2>
        <ol className="list-decimal font-mono max-w-sm ml-6 sm:ml-10 text-sm sm:text-base">
          <li className="mb-4">
            <span className="underline">Write a document</span> Start by
            creating a document within Inkling to share your ideas, plans, or
            reports. You can format your content to match your needs.
          </li>
          <li className="mb-4">
            <span className="underline">Add questions</span> Insert custom
            questions directly into your document to gather feedback. Ask the
            right questions to get the answers you need.
          </li>
          <li className="mb-4">
            <span className="underline">Generate links</span> Once your document
            is ready, generate a unique link to share it with people outside
            your organization, so they can provide feedback.
          </li>
          <li className="mb-4">
            <span className="underline">Track responses</span> Monitor who has
            responded and keep track of incoming feedback in real time, all
            within the Inkling dashboard.
          </li>
          <li className="mb-4">
            <span className="underline">Summarize findings</span> Let
            Inkling&apos;s AI analyze the feedback, giving you key takeaways and
            insights at a glance, helping you make better decisions faster.
          </li>
        </ol>
        <p className="max-w-sm font-mono text-center">
          Inkling lets you give people the context they need to answer the
          questions you have.
        </p>
      </section>
      <section className="flex flex-col sm:flex-row w-full justify-center items-center gap-10 p-6 sm:p-10">
        <div className="flex flex-row gap-10 sm:gap-20 md:gap-40">
          <ThreeDots />
          <ThreeDots />
          <ThreeDots />
        </div>
        <div className="flex flex-row justify-center w-full">
          <ThreeDots className="hidden md:flex" />
        </div>
      </section>
      <section className="flex flex-col lg:flex-row justify-center p-6 sm:p-10 md:p-20 lg:p-40 gap-10 lg:gap-40">
        <div className="flex flex-col gap-40 w-full max-w-6xl">
          <div>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold"
              style={{ fontFamily: "Raleway Variable" }}
            >
              the inkling advantage
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="flex flex-col gap-10 justify-center">
                <AdvantageItem
                  title="Easy distribution"
                  description="Share custom links with prospects, customers, and stakeholders."
                />
                <AdvantageItem
                  title="Always on-brand"
                  description="Customize the look and feel of your documents to match your brand."
                />
                <AdvantageItem
                  title="Intelligent insights"
                  description="Let Inkling's AI analyze the feedback you receive, giving you key takeaways and insights at a glance."
                />
              </div>
              <img
                src="/overview-editor.png"
                alt="inkling demo"
                className="w-full h-auto object-cover rounded-md border border-brandGreen/20 "
              />
            </div>
          </div>
          <div>
            <div className="flex flex-col gap-10 w-full max-w-6xl">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold"
                style={{ fontFamily: "Raleway Variable" }}
              >
                the use cases
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <img
                  src="/overview-image.png"
                  alt="inkling demo"
                  className="w-full h-auto object-cover rounded-md border border-brandBlue/20 "
                />
                <div className="flex flex-col gap-10 justify-center">
                  <AdvantageItem
                    title="Product Management"
                    description="Gather feedback from customers and prospects to inform your roadmap early and often."
                  />
                  <AdvantageItem
                    title="Customer Success"
                    description="Bring customers into the product development process and build relationships that last."
                  />

                  <AdvantageItem
                    title="Enablement & Training"
                    description="Create resources that teach and gather feedback at the same time."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <footer className="h-auto w-full bg-brandRed px-6 sm:px-24 py-6 sm:py-10">
        <div className="max-w-6xl mx-auto h-full flex items-start px-6">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20 w-full">
            <div className="flex flex-col gap-10 flex-1">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl text-brandWhite"
                style={{ fontFamily: "raleway" }}
              >
                Write documents to gather feedback.
              </h2>
              <Link
                to="/sign-up"
                className="bg-brandWhite text-brandRed px-3 py-1 sm:px-4 sm:py-2 rounded-md text-sm hover:underline hover:font-medium transition-all w-fit"
              >
                Sign up
              </Link>
              <p className="text-brandWhite/90 text-sm sm:text-base font-mono pt-2">
                Inkling is a tool to write documents with embedded survey
                questions so you can gather and analyze feedback from customers,
                prospects, and stakeholders.
              </p>
              <p className="text-brandWhite/90 text-sm font-mono pt-2">
                © Inkling {new Date().getFullYear()}
              </p>
            </div>
            <div className="flex flex-col gap-4 md:w-1/3 text-left md:text-right text-brandWhite/90 font-light">
              <Link to="/sign-in" className="hover:underline">
                Sign in
              </Link>
              <Link to="/privacy" className="hover:underline">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:underline">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <Form method="post" action="/home">
          <DialogTitle>Book a demo</DialogTitle>
          <DialogDescription>
            We&apos;d love to show you around. Fill out the form and we&apos;ll
            get back to you soon.
          </DialogDescription>
          <DialogBody>
            <FieldGroup>
              <Field>
                <Label>Your name</Label>
                <Input name="name" placeholder="" />
              </Field>
              <Field>
                <Label>Your email address</Label>
                <Input name="email" type="email" placeholder="" />
              </Field>
            </FieldGroup>
            <input
              type="hidden"
              name="timezone"
              value={Intl.DateTimeFormat().resolvedOptions().timeZone}
            />
          </DialogBody>
          <DialogActions>
            <Button plain onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Send</Button>
          </DialogActions>
        </Form>
      </Dialog>
    </div>
  );
}
